import React, { useState, useEffect, useRef } from "react";
import "./MemoryMatrixForm.css";

import {
  startSession,
  endSession,
  recordMemoryMatrixMouseClicks,
} from "../../services/GameService";

import LevelAdvanceNotice from "../common/LevelAdvanceNotice";
import { usePlayClock } from "../common/usePlayClock";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register the necessary components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title);

// Share of correct clicks as a percentage (0-100) — the scale the level
// promotion/demotion thresholds use, like every other game.
export const accuracyPercent = (clickLog) => {
  if (!clickLog || !clickLog.length) return 0;
  const correct = clickLog.filter((click) => click.isCorrect).length;
  return (correct / clickLog.length) * 100;
};

// One more tile after a perfect puzzle, but always leave at least one empty
// square, or the tile picker could never finish.
export const growTileCount = (tileCount, gridSize) =>
  Math.min(tileCount + 1, gridSize * gridSize - 1);

const CONFIG_KEYS = [
  "initialGridSize",
  "initialActiveTileCount",
  "timeToMemorize",
  "puzzleCount",
  "maximumGridSize",
  "minimumGridSize",
];

// The game settings for a level: the level's level_parameters JSON laid over
// `fallback` (only positive numbers are taken). Missing or malformed level
// parameters leave the fallback unchanged.
export const levelConfig = (level, fallback) => {
  let params = {};
  try {
    params = JSON.parse((level && level.level_parameters) || "{}") || {};
  } catch (e) {
    params = {};
  }
  const config = { ...fallback };
  CONFIG_KEYS.forEach((key) => {
    const v = params[key];
    if (Number.isFinite(v) && v > 0) config[key] = v;
  });
  return config;
};

const MemoryMatrixGame = ({
  initialGridSize = 3,
  initialActiveTileCount = 5,
  timeToMemorize = 2500,
  puzzleCount = 5,
  maximumGridSize = 20,
  minimumGridSize = 3,
}) => {
  // Settings for the game being played. They start from the props (the level
  // the player had when they opened the game) and are refreshed from the
  // session on every start, so a restart after a promotion uses the new level.
  const [config, setConfig] = useState({
    initialGridSize,
    initialActiveTileCount,
    timeToMemorize,
    puzzleCount,
    maximumGridSize,
    minimumGridSize,
  });
  // The in-flight endSession (it also promotes/demotes the level); a restart
  // waits for it so the next game loads the new level.
  const savingRef = useRef(null);
  const [activeTiles, setActiveTiles] = useState([]);
  const [userClicks, setUserClicks] = useState([]);
  const [showActiveTiles, setShowActiveTiles] = useState(true);
  const [gameStatus, setGameStatus] = useState("memorize"); // 'memorize' or 'guess' or 'complete'
  const [currentGridSize, setCurrentGridSize] = useState(initialGridSize);
  const [currentActiveTileCount, setCurrentActiveTileCount] = useState(
    initialActiveTileCount
  );
  const [mistakes, setMistakes] = useState(0);
  const [currentPuzzleCount, setCurrentPuzzleCount] = useState(puzzleCount);
  const [score, setScore] = useState(0.0);
  const [startGame, setStartGame] = useState(false);
  const [sessionId, setSessionId] = useState(null); // State to hold the session ID
  const [isGameOver, setIsGameOver] = useState(false);
  const [lastClick, setLastClick] = useState(null);
  const [clickLog, setClickLog] = useState([]);
  const [chartData, setChartData] = useState(null);
  const playClock = usePlayClock();

  // Generate unique random active squares
  const generateActiveTiles = (
    newGridSize = currentGridSize,
    newActiveTileCount = currentActiveTileCount
  ) => {
    setLastClick(null); //clear up the last click

    const squares = [];
    while (squares.length < newActiveTileCount) {
      const randomSquare = Math.floor(
        Math.random() * newGridSize * newGridSize
      );
      if (!squares.includes(randomSquare)) {
        squares.push(randomSquare);
      }
    }
    setActiveTiles(squares);
  };

  useEffect(() => {}, []);

  //Start the puzzle
  useEffect(() => {
    if (!startGame) return;

    generateActiveTiles();
    setGameStatus("memorize");
    setShowActiveTiles(true);

    // Hide the active squares after timeToMemorize
    const timer = setTimeout(() => {
      setShowActiveTiles(false);
      setGameStatus("guess");
    }, config.timeToMemorize);

    return () => clearTimeout(timer);
  }, [currentPuzzleCount, startGame, config.timeToMemorize]);

  // Handle square click
  const handleSquareClick = (index) => {
    if (gameStatus !== "guess") return;

    if (userClicks.includes(index)) return;

    const isCorrect = activeTiles.includes(index);

    if (!activeTiles.includes(index)) {
      setMistakes(mistakes + 1);
    }

    setUserClicks([...userClicks, index]);

    // Log the click details
    setClickLog([
      ...clickLog,
      {
        index,
        isCorrect,
        timestamp: new Date().toISOString(),
        gridSize: currentGridSize,
        activeTileCount: currentActiveTileCount,
      },
    ]);

    if (userClicks.length + 1 === currentActiveTileCount) {
      setGameStatus("complete");
      setLastClick(index); // Mark this as the last click
    } else {
      setLastClick(null); // Reset last click if not complete yet
    }
  };

  useEffect(() => {
    if (isGameOver) {
      if (sessionId) {
        recordMemoryMatrixMouseClicks(sessionId, clickLog);
      }

      // Calculate accuracy per grid size
      const accuracyData = {};
      clickLog.forEach(({ gridSize, isCorrect }) => {
        if (!accuracyData[gridSize])
          accuracyData[gridSize] = { correct: 0, total: 0 };
        accuracyData[gridSize].total += 1;
        if (isCorrect) accuracyData[gridSize].correct += 1;
      });

      // Prepare data for the chart
      const labels = Object.keys(accuracyData);
      const data = labels.map(
        (gridSize) =>
          (accuracyData[gridSize].correct / accuracyData[gridSize].total) * 100
      );

      setChartData({
        labels,
        datasets: [
          {
            label: "Accuracy (%)",
            data,
            backgroundColor: "rgba(0, 128, 0, 1.0)",
            borderColor: "rgba(75, 192, 192, 0)",
            borderWidth: 1,
          },
        ],
      });

      const accuracy = accuracyPercent(clickLog);
      setScore(accuracy / 100);
      savingRef.current = endSession(sessionId, accuracy, playClock.stop());
    }
  }, [isGameOver, sessionId, playClock]);

  useEffect(() => {
    if (gameStatus === "complete") {
      if (currentPuzzleCount === 1) {
        setIsGameOver(true);
        setStartGame(false); // Enable the "Restart Game" button after game over
      } else {
        // Add a timeout to hold the view for 2 seconds
        const timer = setTimeout(() => {
          let newGridSize = currentGridSize;
          let newActiveTileCount = currentActiveTileCount;

          if (mistakes === 0) {
            newGridSize = Math.min(currentGridSize + 1, config.maximumGridSize);
            newActiveTileCount = growTileCount(newActiveTileCount, newGridSize);
          } else if (mistakes > 2) {
            newGridSize = Math.max(currentGridSize - 1, config.minimumGridSize);
            newActiveTileCount = Math.max(currentActiveTileCount - 1, 1);
          } else if (mistakes === 1) {
            newActiveTileCount = Math.max(currentActiveTileCount - 1, 1);
          }

          setCurrentGridSize(newGridSize);
          setCurrentActiveTileCount(newActiveTileCount);
          setMistakes(0);
          setCurrentPuzzleCount(currentPuzzleCount - 1);
          setUserClicks([]);
          setShowActiveTiles(true);
          setGameStatus("memorize");

          generateActiveTiles(newGridSize, newActiveTileCount);
        }, 2000); // 2 seconds delay

        return () => clearTimeout(timer);
      }
    }
  }, [gameStatus, currentPuzzleCount]);

  // Determine if a square is active, clicked correctly, or clicked incorrectly
  const getSquareStatus = (index) => {
    if (index === lastClick) {
      if (activeTiles.includes(index)) {
        return "last-correct";
      } else {
        return "last-incorrect";
      }
    }

    if (showActiveTiles && activeTiles.includes(index)) return "active";
    if (userClicks.includes(index) && activeTiles.includes(index))
      return "correct";
    if (userClicks.includes(index) && !activeTiles.includes(index))
      return "incorrect";

    // When game is complete, reveal all active squares
    if (gameStatus === "complete" && activeTiles.includes(index))
      return "correct";

    return "default";
  };

  const setInitialState = (cfg) => {
    setCurrentPuzzleCount(cfg.puzzleCount);
    setCurrentGridSize(cfg.initialGridSize);
    setCurrentActiveTileCount(cfg.initialActiveTileCount);
    setMistakes(0);
    setUserClicks([]);
    setScore(0);
    setIsGameOver(false);
    setLastClick(null);
    setStartGame(true);
    setClickLog([]);
  };

  const handleStartOrRestart = async () => {
    // Let the last game's save (and any level change it makes) land first.
    if (savingRef.current) {
      try {
        await savingRef.current;
      } catch (e) {
        // a failed save shouldn't stop the next game
      }
      savingRef.current = null;
    }
    const session = await startSession("memory-matrix", config);
    if (session && session.sessionId) {
      setSessionId(session.sessionId); // Store the session ID if valid
    }
    // Play the player's current level, which may have changed since the page
    // was opened.
    const cfg = levelConfig(session && session.level, config);
    setConfig(cfg);
    setInitialState(cfg);
    playClock.start();
  };

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <div>
        <h1>Memory Matrix Game</h1>
        <div>
          <p>
            Grid Size: {currentGridSize}, Tile Count: {currentActiveTileCount},
            Remaining puzzles: {currentPuzzleCount}
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${currentGridSize}, 100px)`,
            gap: "10px",
          }}
        >
          {Array.from(
            { length: currentGridSize * currentGridSize },
            (_, index) => (
              <div
                key={index}
                className={`memory-square ${getSquareStatus(index)}`} // Apply status class
                data-status={getSquareStatus(index)}
                onClick={() => handleSquareClick(index)}
                style={{
                  transform:
                    getSquareStatus(index) === "active" ||
                    getSquareStatus(index) === "correct" ||
                    getSquareStatus(index) === "incorrect"
                      ? "rotateX(180deg)"
                      : "rotateX(0deg)",
                }}
              />
            )
          )}
        </div>
        {gameStatus === "complete" && currentPuzzleCount === 1 && (
          <div>
            <h2>Game Over!</h2>

            {chartData && (
              <div>
                <h3>Detailed accuracy for different grid sizes</h3>
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: "top",
                      },
                      title: {
                        display: true,
                        text: "Accuracy by Grid Size",
                      },
                    },
                  }}
                />
              </div>
            )}
            <p>Your average accuracy is {(score * 100).toFixed(2)}%</p>
            <LevelAdvanceNotice />
          </div>
        )}
        <button
          onClick={handleStartOrRestart}
          disabled={startGame}
          style={{
            padding: "10px 20px",
            fontSize: "16px",
            backgroundColor: startGame ? "#d3d3d3" : "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: startGame ? "not-allowed" : "pointer",
            marginTop: "20px",
          }}
        >
          {currentPuzzleCount === 1 ? "Restart a New Game" : "Start a Game"}
        </button>
      </div>
    </div>
  );
};

export default MemoryMatrixGame;
